import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import CreateOrgModal from "../../components/organization/CreateOrgModal";
import EditOrgModal from "../../components/organization/EditOrgModal";
import OrganizationsTable from "../../components/organization/OrganizationsTable";
import Pagination from "../../components/organization/Pagination";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { canWrite } from "../../components/auth/perm";

// کمک: debounce محلی سبک
function useDebouncedValue(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const OrgOrganizationsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const token = currentUser?.token;

  // ✅ تغییر پرمیژن: از users → organizations
  const mayCreate = canWrite(currentUser, "organizations"); // expects "org:organizations:write"

  // لیست و صفحه‌بندی
  const [items, setItems] = useState([]); // سازمان‌ها
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  // فیلترها (ساده‌تر از کاربران: q و sort کافی است)
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("-createdAt"); // createdAt نزولی
  const qDebounced = useDebouncedValue(q, 500);

  // مدال‌ها
  const [showCreate, setShowCreate] = useState(false);
  const [editOrg, setEditOrg] = useState(null);

  // ⚡ fetch سازمان‌ها
  const fetchOrganizations = async (overwritePage) => {
    const finalPage = overwritePage ?? page;
    setLoading(true);
    try {
      const params = {
        q: qDebounced || "",
        sort,             // مثال: "-createdAt", "name"
        page: finalPage,
        limit,
      };

      // ✅ Endpoint جدید
      const { data } = await httpRequest("GET", "/organization", params, {
        "x-access-token": token,
      });

      // انتظار پاسخ: { data: { items: [], total, pages, page } } یا { data: [] }
      const payload = data?.data || {};
      const list = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
      setItems(list);

      setTotal(payload.total || list.length || 0);
      setPages(payload.pages || 1);
      setPage(payload.page || finalPage);
    } catch (e) {
      console.error("fetchOrganizations error:", e);
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrganizations(1); }, [qDebounced, sort, limit]); // تغییر فیلترهای اصلی
  useEffect(() => { fetchOrganizations(); }, []); // بار اول

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${isRtl ? "md:mr-64" : "md:ml-64"}`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"}`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_orgs.page_title", "مدیریت سازمان‌ها")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("org_orgs.page_header", "سازمان‌ها")}
            </h1>
            {mayCreate && (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus size={18} />
                {t("org_orgs.create_btn", "ایجاد سازمان")}
              </button>
            )}
          </div>

          <div className="mt-4">
            <OrganizationsTable
              items={items}
              loading={loading}
              isRtl={isRtl}
              onEdit={(org) => setEditOrg(org)}
              currentUser={currentUser}
              // نمایش ستون‌ها:
              // name, brandCode, owner (name/email), membersCount, createdAt
              // اکشن‌ها: Edit، Manage Members (اگر خواستی)
            />
          </div>

          <Pagination
            page={page}
            pages={pages}
            onChange={(p) => {
              setPage(p);
              fetchOrganizations(p);
            }}
          />
        </main>
      </div>

      {/* Modals */}
      <CreateOrgModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchOrganizations(1);
        }}
      />

      <EditOrgModal
        organization={editOrg}
        isOpen={!!editOrg}
        onClose={() => setEditOrg(null)}
        onSaved={() => {
          setEditOrg(null);
          fetchOrganizations(page);
        }}
      />
    </div>
  );
};

export default OrgOrganizationsPage;
